import React from "react";
import { CheckCircle2, Wrench, Sparkles, PenLine } from "lucide-react";

export const SKILL_LABELS = {
  reading: "Compréhension écrite",
  listening: "Compréhension orale",
  writing: "Expression écrite",
  speaking: "Expression orale",
};
export const SKILL_SHORT = { reading: "Reading", listening: "Listening", writing: "Writing", speaking: "Speaking" };
export const SKILL_ORDER = ["reading", "listening", "writing", "speaking"];

export function clbTone(clb, target) {
  if (clb == null) return "text-muted-foreground";
  if (clb >= target) return "text-primary";
  if (clb >= target - 1) return "text-amber-600";
  return "text-destructive";
}

export function FormattedText({ text, className = "", strongClassName = "text-foreground" }) {
  if (!text) return null;
  const parts = String(text).split(/(\*\*[^*]+\*\*)/g);
  return (
    <span className={`whitespace-pre-wrap ${className}`}>
      {parts.map((p, i) =>
        p.startsWith("**") && p.endsWith("**") ? <strong key={i} className={`font-semibold ${strongClassName}`}>{p.slice(2, -2)}</strong> : <span key={i}>{p}</span>
      )}
    </span>
  );
}

export function LevelBadge({ cefr, clb }) {
  return (
    <div className="flex items-center gap-3" data-testid="level-estimate">
      <div className="h-14 w-14 shrink-0 rounded-2xl emerald-surface grid place-items-center text-white">
        <div className="text-center leading-none">
          <div className="text-[9px] uppercase tracking-wide opacity-80">CLB</div>
          <div className="font-display text-xl font-semibold">{clb ?? "–"}</div>
        </div>
      </div>
      <div>
        <div className="text-sm text-muted-foreground">Estimated level (tentative)</div>
        <div className="text-lg font-display font-semibold text-primary">Around CLB {clb}{cefr ? ` · ${cefr}` : ""}</div>
      </div>
    </div>
  );
}

export function FeedbackReport({ result, userText }) {
  if (!result) return null;
  return (
    <div className="space-y-5" data-testid="feedback-report">
      <div className="rounded-2xl bg-card border border-border p-5 flex flex-wrap items-center justify-between gap-4">
        <LevelBadge cefr={result.cefr} clb={result.clb} />
        {"word_ok" in result && (
          <div className={`text-sm font-semibold ${result.word_ok ? "text-primary" : "text-destructive"}`}>
            {result.word_count} mots {result.word_ok ? "✓" : "· sous le minimum requis"}
          </div>
        )}
      </div>

      {userText && (
        <div className="rounded-2xl bg-card border border-border p-5" data-testid="your-answer-card">
          <h4 className="font-semibold mb-2 text-sm flex items-center gap-2"><PenLine className="h-4 w-4 text-primary" /> Your answer</h4>
          <p className="text-sm leading-relaxed text-foreground whitespace-pre-wrap">{userText}</p>
        </div>
      )}

      {result.summary && (
        <div className="rounded-2xl bg-accent/60 border border-border p-4 text-sm">
          <FormattedText text={result.summary} />
        </div>
      )}

      {result.strengths?.length > 0 && (
        <div className="rounded-2xl bg-card border border-border p-5">
          <h4 className="font-semibold mb-2 text-sm flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-primary" /> Points forts</h4>
          <ul className="space-y-1.5 text-sm text-muted-foreground">
            {result.strengths.map((s, i) => <li key={`str-${i}`}>• {s}</li>)}
          </ul>
        </div>
      )}

      {result.fixes?.length > 0 && (
        <div className="rounded-2xl bg-card border border-border p-5">
          <h4 className="font-semibold mb-3 text-sm flex items-center gap-2"><Wrench className="h-4 w-4 text-primary" /> How to reach the next level</h4>
          <div className="space-y-3">
            {result.fixes.map((f, i) => (
              <div key={`fix-${i}`} className="rounded-xl bg-background border border-border p-3 text-sm">
                <p className="text-muted-foreground">{f.issue}</p>
                <p className="mt-1"><span className="font-semibold text-primary">→ </span><FormattedText text={f.fix} /></p>
                {f.why && <p className="text-xs text-muted-foreground mt-1 italic">{f.why}</p>}
              </div>
            ))}
          </div>
        </div>
      )}

      {result.pronunciation_flags?.length > 0 && (
        <div className="rounded-2xl bg-card border border-border p-5">
          <h4 className="font-semibold mb-2 text-sm">🗣️ Prononciation</h4>
          <ul className="space-y-1.5 text-sm text-muted-foreground">
            {result.pronunciation_flags.map((s, i) => <li key={`pron-${i}`}>• {s}</li>)}
          </ul>
        </div>
      )}

      {(result.rewrite_clb5 || result.rewrite_clb7) && (
        <div className="grid md:grid-cols-2 gap-4">
          {result.rewrite_clb5 && (
            <div className="rounded-2xl bg-card border border-border p-5" data-testid="rewrite-clb5">
              <h4 className="font-semibold mb-2 text-sm flex items-center gap-2"><Sparkles className="h-4 w-4 text-primary" /> Your answer at CLB 5 (B1)</h4>
              <p className="text-sm leading-relaxed text-muted-foreground"><FormattedText text={result.rewrite_clb5} /></p>
            </div>
          )}
          {result.rewrite_clb7 && (
            <div className="rounded-2xl emerald-surface grain relative overflow-hidden p-5 text-white" data-testid="rewrite-clb7">
              <h4 className="font-semibold mb-2 text-sm flex items-center gap-2"><Sparkles className="h-4 w-4" /> Your answer at CLB 7 (B2)</h4>
              <p className="text-sm leading-relaxed text-white/95"><FormattedText text={result.rewrite_clb7} strongClassName="text-amber-200" /></p>
              <p className="text-xs text-white/70 mt-3">Same ideas as yours — upgraded. Learn the bold phrases and reuse the pattern.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
